import { useEffect, useRef } from 'react';
import { useApp } from '../AppProvider';
import { useClientStore } from '../../lib/stores/clientStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { resolveDefaultActivityAssignee } from '../../lib/utils/defaultActivityAssignee';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';
import { saveLeadNoteWithAttachments, triggerLeadActivityWorkflows } from './leadDrawerWrites';
import type { EntityAdapter, SavedActivity } from '../shared/activity-drawer/types';

export interface LeadDrawerAdapterParams {
  isOpen: boolean;
  leadId: string;
  currentStage: string;
  currentAssignee?: string;
  currentSalesRepId?: string;
  leadPipelineId?: string | null;
  pipelineContext?: string;
  requireNote: boolean;
  /** Stato LIVE dello slot `statusSection` del wrapper. */
  newStage: string;
  newAssignee: string;
  lostReason: string;
  invitedCommercial: string;
  selectedStageIsLost: boolean;
  onStageUpdated: () => void;
  onNotesUpdated: () => void | Promise<void>;
  onAssigneeUpdated?: () => void;
}

/**
 * Adapter entità "lead" per il drawer condiviso Attività/Nota: raccoglie TUTTE le
 * scritture e i side effect lead-specifici che nella fonte
 * (`LeadStatusUpdateDrawer.tsx`, versione pre-refactor) vivevano dentro `handleSubmit`.
 *
 * Mapping fonte → qui:
 * - `:996-1035`  commit fase/motivo perso/assegnatario/commerciale → `commitEntityChanges`
 * - `:970-973`   guardia "motivo della perdita obbligatorio"       → `validateEntityChanges`
 * - `:1038-1066` nota + allegati                                    → `saveNote`
 * - `:1387-1398` `lead_id` come UNICO campo di collegamento         → `linkageFields`
 * - `:1415-1461` branching workflow lead/cliente (solo in INSERT)   → `afterActivitySaved`
 * - `:1687-1695` toast di esito fase/assegnatario                   → in coda a `commitEntityChanges`
 * - `:1703-1708` motivo perso nel flusso `requireNote`              → `handleNotesUpdated`
 * - `:1715-1727` `onStageUpdated`/`onAssigneeUpdated` dopo la nota  → `handleNotesUpdated`
 *
 * Con `requireNote` (drag&drop Kanban) la shell NON chiama `commitEntityChanges`: il
 * commit della fase resta al consumer dentro `onNotesUpdated` (contratto invariato).
 * Il commerciale invece viene committato dal wrapper in ENTRAMBI i flussi — nella
 * fonte `:1030-1035` era scritto fuori dal ramo `if (!requireNote)`, quindi senza
 * questo commit il cambio si perderebbe nel flusso Kanban.
 */
export function useLeadDrawerAdapter(params: LeadDrawerAdapterParams): {
  adapter: EntityAdapter;
  handleNotesUpdated: () => Promise<void>;
} {
  const {
    isOpen,
    leadId,
    currentStage,
    currentAssignee,
    currentSalesRepId,
    leadPipelineId,
    pipelineContext,
    requireNote,
    newStage,
    newAssignee,
    lostReason,
    invitedCommercial,
    selectedStageIsLost,
    onStageUpdated,
    onNotesUpdated,
    onAssigneeUpdated,
  } = params;

  const { toast } = useApp();
  const { updateClient } = useClientStore();
  const defaultAssigneeSetting = useSettingsStore((s) => s.settings?.activity_default_assignee ?? null);
  const { createNote, uploadNoteAttachment } = useLeadNoteStore();
  const { lead: leadLabel } = useMenuLabels();

  // Cosa è cambiato nell'ultimo commit: serve a chiamare `onStageUpdated`/
  // `onAssigneeUpdated` DOPO `onNotesUpdated`, come la fonte (:1715-1727).
  const committedChangesRef = useRef({ stage: false, assignee: false });
  // Ultimo valore di `sales_rep_id` già scritto in questo submit: il commit del
  // commerciale è invocato sia da `commitEntityChanges` sia da `handleNotesUpdated`
  // (per coprire il flusso `requireNote`) e non deve scrivere due volte.
  const committedSalesRepRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    committedChangesRef.current = { stage: false, assignee: false };
    committedSalesRepRef.current = null;
  }, [isOpen]);

  /**
   * Commit del commerciale assegnato. Fonte `:1031-1035`: la scrittura era
   * INCONDIZIONATA ad ogni salvataggio (anche senza modifiche) — qui avviene solo se
   * il valore è davvero cambiato rispetto a `currentSalesRepId`. Come nella fonte, la
   * selezione di "Nessuno" (valore vuoto) NON azzera il commerciale già assegnato.
   */
  const commitSalesRepIfChanged = async () => {
    if (!invitedCommercial || invitedCommercial === (currentSalesRepId || '')) return;
    if (committedSalesRepRef.current === invitedCommercial) return;
    await updateClient(leadId, { sales_rep_id: invitedCommercial });
    committedSalesRepRef.current = invitedCommercial;
  };

  const adapter: EntityAdapter = {
    kind: 'lead',
    entity: { id: leadId },
    // Per il lead entità e cliente coincidono: è la stessa riga di `clients`.
    clientId: leadId,
    entityUrl: () => `${window.location.origin}/leads/${leadId}`,
    // Fonte :1387-1398 — l'attività di un lead porta SOLO `lead_id`, mai `client_id`.
    linkageFields: () => ({ lead_id: leadId }),
    // Impostazione di organizzazione (6bcec0bd): senza, resta il proprietario del Lead.
    defaultAssigneeId: resolveDefaultActivityAssignee(defaultAssigneeSetting, newAssignee || currentAssignee || null),
    leadOwnerId: newAssignee || currentAssignee || null,
    validateEntityChanges: () => {
      // Fonte :952 (`if (!leadId) return;`): senza id non si scrive nulla. Qui la guardia
      // è esplicita invece che silenziosa, così l'utente capisce perché non salva.
      if (!leadId) return 'Lead non disponibile: riprova';
      return selectedStageIsLost && !lostReason ? 'Seleziona il motivo della perdita' : null;
    },

    commitEntityChanges: async () => {
      const stageChanged = newStage !== currentStage;
      // Fonte `:996`: `newAssignee !== currentAssignee` marcava come "cambiato" anche
      // il caso lead senza assegnatario + select vuota (`'' !== undefined`), scrivendo
      // `assigned_to: ''` su una colonna uuid. Qui i vuoti sono normalizzati.
      const assigneeChanged = newAssignee !== (currentAssignee || '');

      if (stageChanged) {
        const now = new Date().toISOString();
        const stageUpdate: Record<string, any> = leadPipelineId
          ? { pipeline_stage_id: newStage, last_activity_date: now }
          : { lead_stage: newStage, last_activity_date: now };
        if (selectedStageIsLost && lostReason) {
          stageUpdate.lost_reason = lostReason;
          stageUpdate.lost_reason_date = now;
        } else if (!selectedStageIsLost) {
          stageUpdate.lost_reason = null;
          stageUpdate.lost_reason_date = null;
        }
        await updateClient(leadId, stageUpdate);
      }

      if (assigneeChanged) {
        await updateClient(leadId, { assigned_to: newAssignee || null });
      }

      await commitSalesRepIfChanged();
      // I toast di esito NON si emettono qui: il commit dell'entità precede nota e
      // attività, e un fallimento successivo lascerebbe a schermo "Fase aggiornata con
      // successo" seguito dall'errore. Escono in `handleNotesUpdated`, a salvataggio
      // completato (fonte :1687-1695, che stava dopo nota e attività).
      committedChangesRef.current = { stage: stageChanged, assignee: assigneeChanged };
    },

    // Fonte :1038-1066 — nota + allegati (upload uno per uno, esito riassunto a toast).
    // Corpo estratto in `leadDrawerWrites.ts` per essere riusato dal composer inline della
    // cronologia: stessa funzione, stessi argomenti, stesso comportamento.
    saveNote: async (html: string, files: File[]) => {
      await saveLeadNoteWithAttachments({
        leadId,
        html,
        files,
        pipelineContext,
        createNote,
        uploadNoteAttachment,
        toast,
      });
    },

    /**
     * Fonte :1415-1461 — i workflow di automazione scattano SOLO alla creazione
     * dell'attività (`meta.isInsert`), mai in modifica, e si biforcano su
     * `client_states.is_lead` della riga `clients` collegata.
     */
    afterActivitySaved: async (activity: SavedActivity, safeType: string, meta: { isInsert: boolean }) => {
      if (!meta.isInsert) return;
      await triggerLeadActivityWorkflows(leadId, activity, safeType);
    },

    salesRep: {
      current: invitedCommercial || null,
      // Oggi nessuno la chiama (la shell usa solo `current`), ma deve passare dalla
      // STESSA guardia di `commitEntityChanges`: scrivere `sales_rep_id` senza il
      // controllo "solo se cambiato" reintrodurrebbe la scrittura incondizionata
      // della fonte (:1031-1035). L'unico valore committabile è quello dello slot,
      // quindi l'argomento è ignorato di proposito.
      onCommit: async (_id: string) => {
        await commitSalesRepIfChanged();
      },
    },
  };

  /**
   * `onNotesUpdated` del consumer, avvolta: nel flusso `requireNote` il commit della
   * fase resta al consumer (contratto attuale) ma motivo perso (fonte :1703-1708) e
   * commerciale devono comunque essere scritti prima. Dopo la callback del consumer si
   * notificano fase/assegnatario, nello stesso ordine della fonte (:1712-1727).
   */
  const handleNotesUpdated = async () => {
    if (requireNote && selectedStageIsLost && lostReason) {
      await updateClient(leadId, {
        lost_reason: lostReason,
        lost_reason_date: new Date().toISOString(),
      });
    }
    await commitSalesRepIfChanged();

    await onNotesUpdated();

    if (requireNote) return;

    const { stage: stageChanged, assignee: assigneeChanged } = committedChangesRef.current;
    if (stageChanged) onStageUpdated();
    if (assigneeChanged) onAssigneeUpdated?.();

    // Toast di esito (fonte :1687-1695): emessi solo ora, a nota/attività già salvate.
    // Nella fonte precedevano `onNotesUpdated()` di una manciata di righe; qui li
    // seguono, così un errore a monte non li fa mai comparire.
    if (stageChanged && assigneeChanged) {
      toast.success('Fase e assegnatario aggiornati con successo');
    } else if (stageChanged) {
      toast.success(`Fase ${leadLabel.del}${leadLabel.singularLower} aggiornata con successo`);
    } else if (assigneeChanged) {
      toast.success('Assegnatario aggiornato con successo');
    }

    committedChangesRef.current = { stage: false, assignee: false };
  };

  return { adapter, handleNotesUpdated };
}
